import React, { useState } from 'react';
import { OV_STRINGS } from '../../constants/strings';
import { formatDate, formatDateTime, formatDday, isExpired, addDays } from '../../utils/date';
import FileUploadSection from '../FileUploadSection/FileUploadSection';
import styles from './VerificationStatusModal.module.scss';

const STATUS_CONFIG = {
  in_review: {
    badge: '검토중',
    title: '검토중 입니다',
    desc: '디스코에서 내용 검토 후 반영 예정입니다. 혹시 빠트린 서류가 있다면 꼭 첨부해주세요',
    cta: '확인',
    ctaAction: 'close',
  },
  completed: {
    badge: '인증 완료',
    title: '인증이 완료되었습니다',
    desc: '매물에 인증뱃지가 표시되고 상단에 노출됩니다.',
    cta: '확인',
    ctaAction: 'close',
  },
  renewal: {
    badge: '인증 완료',
    title: '곧 소유자 인증이 만료됩니다',
    desc: '인증이 만료되면 인증뱃지는 제거됩니다.',
    cta: '인증 갱신하기',
    ctaAction: 'renew',
  },
  expired: {
    badge: '인증 만료',
    title: '소유자 인증이 만료되었습니다',
    desc: '인증 갱신후에 다시 뱃지를 달 수 있습니다.',
    cta: '인증 갱신하기',
    ctaAction: 'renew',
  },
  failed: {
    badge: '인증 실패',
    title: '소유자 인증을 실패했습니다',
    desc: '아래의 사유를 확인해주세요',
    cta: '재인증 요청',
    ctaAction: 'renew',
    secondaryCta: '이의 신청',
    secondaryCtaAction: 'dispute',
  },
  rejected: {
    badge: '반려',
    badgeActive: '인증 반려',
    title: '요청이 반려되었습니다',
    desc: '아래의 사유를 확인한 뒤, 필요한 서류 보완 후 다시 제출해주세요\n반려 후 재요청은 1주일 까지 가능합니다',
    cta: '재인증 요청',
    ctaAction: 'renew',
    ctaSubmit: '제출하기',
    secondaryCta: '이의 신청',
    secondaryCtaAction: 'dispute',
  },
};

function VerificationStatusModal({
  status = 'in_review',
  verification = {},
  attachments = [],
  reason,
  detail,
  onClose,
  onRenew,
  onResubmit,
  onDispute,
  disputeUuid,
  disputeData,
  onUpload,
  onDeleteFile,
  onResend,
  onDirectShare,
  linkUrl,
  remainingSends = 0,
}) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.in_review;
  const [supplementFiles, setSupplementFiles] = useState([]);
  const [showDispute, setShowDispute] = useState(false);
  const [showDisputeResult, setShowDisputeResult] = useState(false);
  const [submittedDisputeData, setSubmittedDisputeData] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeLoading, setDisputeLoading] = useState(false);

  const hasDispute = !!(disputeData || submittedDisputeData);
  const activeDisputeData = submittedDisputeData || disputeData;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && e.isTrusted) onClose?.();
  };

  const handleCta = () => {
    if (config.ctaAction === 'renew') onRenew?.();
    else if (config.ctaAction === 'resubmit') onResubmit?.(supplementFiles);
    else if (supplementFiles.length > 0) onResubmit?.(supplementFiles);
    else onClose?.();
  };


  const isResubmitExpired = status === 'rejected'
    && verification.resubmit_deadline
    && isExpired(verification.resubmit_deadline);

  const showAttachments = status === 'in_review' || status === 'rejected';
  const showUpload = status === 'in_review' || (status === 'rejected' && !isResubmitExpired);
  const showValidityCard = status === 'completed' || status === 'renewal' || status === 'expired';
  const showInfoRows = status === 'completed' || status === 'renewal' || status === 'expired';
  const showRenewalNote = status === 'completed' || status === 'renewal' || status === 'expired';

  // 제출된 이의 신청 결과 화면
  if (showDisputeResult && activeDisputeData) {
    return (
      <>
        <div className={styles.overlay} onClick={handleOverlayClick} />
        <div className={styles.modalContent}>
          <div className={styles.modal}>
            <div className={styles.nav}>
              <div className={styles.navLeft}>
                <span className={`${styles.badgeBase} ${styles.badgeRed}`}>이의 신청</span>
              </div>
              <button type="button" className={styles.closeBtn} onClick={() => setShowDisputeResult(false)} aria-label="뒤로">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="#171719" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className={styles.header}>
              <h2 className={styles.title}>제출된 이의 신청</h2>
              <p className={styles.desc}>기입하신 정보로 이의신청이 접수되었습니다.</p>
            </div>
            <div className={styles.scrollContent}>
              <div className={styles.disputeResultFields}>
                <div className={styles.disputeResultField}>
                  <label className={styles.disputeResultLabel}>
                    이의신청 내용 <span className={styles.disputeResultRequired}>*</span>
                  </label>
                  <div className={styles.disputeResultTextarea}>
                    {activeDisputeData.reason || ''}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.actionArea}>
              <button
                type="button"
                className={styles.ctaButtonAlt}
                onClick={() => setShowDisputeResult(false)}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // 이의신청 화면
  if (showDispute) {
    const canSubmitDispute = disputeReason.trim().length > 0;
    const handleDisputeSubmit = async () => {
      if (!canSubmitDispute || disputeLoading) return;
      setDisputeLoading(true);
      try {
        await onDispute?.({ uuid: disputeUuid, reason: disputeReason });
        setSubmittedDisputeData({ reason: disputeReason });
        setShowDispute(false);
        setShowDisputeResult(true);
      } finally {
        setDisputeLoading(false);
      }
    };

    return (
      <>
        <div className={styles.overlay} onClick={handleOverlayClick} />
        <div className={styles.modalContent}>
          <div className={styles.modal}>
            <div className={styles.nav}>
              <div className={styles.navLeft}>
                <span className={`${styles.badgeBase} ${styles.badgeRed}`}>이의 신청</span>
              </div>
              <button type="button" className={styles.closeBtn} onClick={() => setShowDispute(false)} aria-label="뒤로">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="#171719" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className={styles.header}>
              <h2 className={styles.title}>이의 신청</h2>
              <p className={styles.desc}>아래 기입하신 정보로 이의신청이 접수됩니다.</p>
            </div>
            <div className={styles.scrollContent}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                <label style={{ fontSize: 14, fontWeight: 500, color: '#171719' }}>
                  이의신청 내용 <span style={{ color: '#d92d20' }}>*</span>
                </label>
                <textarea
                  value={disputeReason}
                  onChange={(e) => setDisputeReason(e.target.value)}
                  placeholder="소유자가 인증을 했는데 인증되지 않았습니다..."
                  rows={5}
                  style={{
                    width: '100%', flex: 1, minHeight: 120, padding: '12px 16px', border: '1px solid rgba(112,115,124,0.16)',
                    borderRadius: 10, fontSize: 15, color: '#171719', outline: 'none', background: '#fff',
                    fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
            <div className={styles.actionArea}>
              <button
                type="button"
                className={canSubmitDispute ? styles.ctaButton : styles.ctaButtonDisabled}
                disabled={!canSubmitDispute || disputeLoading}
                onClick={handleDisputeSubmit}
              >
                {disputeLoading ? '제출 중...' : '제출하기'}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.overlay} onClick={handleOverlayClick} />
      <div className={styles.modalContent}>
        <div className={styles.modal}>

          <div className={styles.nav}>
            <div className={styles.navLeft}>
              <span className={`${styles.badgeBase} ${
                status === 'in_review' ? styles.badgeCyan
                : status === 'failed' ? styles.badgeRed
                : status === 'rejected' ? styles.badgeOrange
                : status === 'expired' ? styles.badgeGray
                : styles.badge
              }`}>
                {(status === 'rejected' && config.badgeActive) ? config.badgeActive : config.badge}
              </span>
              {status === 'in_review' && (
                <span className={`${styles.badgeBase} ${
                  verification.identity_verified ? styles.badgeGreen : styles.badgeOrange
                }`}>
                  {verification.identity_verified ? '인증됨' : '인증전'}
                </span>
              )}
            </div>
            <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="닫기">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="#171719" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className={styles.header}>
            <h2 className={styles.title}>
              {verification.failed_title || config.title}
            </h2>
            <p className={styles.desc}>
              {status === 'failed' ? (verification.failed_desc || config.desc)
                : (status === 'rejected') ? config.desc
                : (reason || config.desc)}
            </p>
          </div>


          <div className={styles.scrollContent}>

            {showValidityCard && (
              <div className={styles.validityCard}>
                <div className={styles.validityLeft}>
                  <span className={styles.validityLabel}>인증 배지 유효 기간</span>
                  <strong className={styles.validityDate}>
                    {formatDateTime(verification.verified_expires_at)}까지
                  </strong>
                  <span className={styles.validitySubLabel}>
                    인증 완료일: {formatDate(verification.verified_at)}
                  </span>
                </div>
                {status !== 'expired' && (
                  <div className={styles.validityRight}>
                    <span className={styles.validityDdayLabel}>만료까지</span>
                    <strong className={styles.validityDday}>
                      {formatDday(verification.verified_expires_at)}
                    </strong>
                  </div>
                )}
              </div>
            )}


            {showInfoRows && (
              <div className={styles.infoRows}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>
                    {status === 'expired' ? '마지막 인증 방법' : '인증 방법'}
                  </span>
                  <span className={styles.infoValue}>
                    {verification.verification_method || '소유자 직접 인증'}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>
                    {status === 'expired' ? '마지막 인증 완료일' : '인증 완료일'}
                  </span>
                  <span className={styles.infoValue}>
                    {formatDate(verification.verified_at)}
                  </span>
                </div>
              </div>
            )}


            {showRenewalNote && (
              <div className={styles.renewalNote}>
                <strong className={styles.renewalTitle}>갱신 안내</strong>
                <p className={styles.renewalDesc}>
                  {status === 'renewal'
                    ? '갱신 완료 시 기존 만료일에서 30일이 추가됩니다.'
                    : `만료 7일 전(${addDays(verification.verified_expires_at, -7)})부터 갱신 권고 알림이 발송됩니다. 만료 전에 갱신하면 인증 배지가 중단 없이 유지됩니다.`}
                </p>
              </div>
            )}


            {status === 'failed' && (
              <>
                <div className={styles.failReasonCard}>
                  <strong className={styles.failReasonTitle}>실패 사유</strong>
                  <p className={styles.failReasonText}>{reason || config.desc}</p>
                  <span className={styles.failReasonDate}>
                    인증 시도일: {formatDate(verification.failed_at || verification.created_at)}
                  </span>
                </div>
                {verification.can_dispute && verification.dispute_deadline && (
                  <div className={styles.disputeCard}>
                    <div className={styles.disputeLeft}>
                      <span className={styles.disputeLabel}>이의 신청 기한</span>
                      <strong className={styles.disputeDate}>
                        {formatDate(verification.dispute_deadline)}까지
                      </strong>
                      <span className={styles.disputeSub}>실패일로부터 7일 이내</span>
                    </div>
                    <div className={styles.disputeRight}>
                      <span className={styles.disputeDdayLabel}>남은 기간</span>
                      <strong className={styles.disputeDday}>
                        {formatDday(verification.dispute_deadline)}
                      </strong>
                    </div>
                  </div>
                )}
              </>
            )}


            {status === 'rejected' && reason && (
              <div className={styles.rejectionReasonCard}>
                <strong className={styles.rejectionReasonTitle}>{reason}</strong>
                {detail && <p className={styles.rejectionReasonDesc}>- {detail}</p>}
              </div>
            )}


            {showAttachments && attachments.length > 0 && (
              <div className={styles.attachSection}>
                <div className={styles.attachHeader}>
                  <span className={styles.attachTitle}>제출된 서류</span>
                  <span className={styles.attachCount}>첨부 파일: {attachments.length}/10개</span>
                </div>
                <div className={styles.attachList}>
                  {attachments.map((file, i) => (
                    <div key={i} className={styles.attachItem}>
                      <span className={styles.attachName}>{file.original_filename}</span>
                      <div className={styles.attachRight}>
                        <span className={styles.attachDate}>{formatDate(file.uploaded_at)}</span>
                        {onDeleteFile && (
                          <button type="button" className={styles.attachDelete} onClick={() => onDeleteFile(file.id)} aria-label="삭제">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke="#989ba2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}


            {showUpload && (
              <FileUploadSection
                title="보완서류 추가"
                files={supplementFiles}
                onFilesChange={setSupplementFiles}
              />
            )}


            {status === 'in_review' && linkUrl && (
              <div className={styles.resendSection}>
                <div className={styles.resendIcon}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M1.778 6.818c-.25-.179-.375-.268-.484-.28a.417.417 0 00-.407.21c-.054.096-.054.247-.054.551v5.402c0 .671 0 1.225.037 1.676.038.468.12.899.327 1.303a2.917 2.917 0 001.457 1.457c.404.206.835.288 1.303.327.451.037 1.005.037 1.676.037h8.735c.671 0 1.225 0 1.676-.037.468-.038.899-.12 1.303-.327a2.917 2.917 0 001.457-1.457c.206-.404.288-.835.327-1.303.037-.451.037-1.005.037-1.676V7.3c0-.15 0-.224-.014-.276a.417.417 0 00-.546-.286c-.05.018-.112.061-.236.147l-6.452 4.467a3.75 3.75 0 01-1.316.707 2.083 2.083 0 01-1.213-.01 3.75 3.75 0 01-1.305-.726L1.778 6.818z" fill="#171719" />
                    <path d="M18.444 4.807c.154-.106.231-.16.278-.245a.417.417 0 00.05-.247.417.417 0 00-.135-.29 2.917 2.917 0 00-1.29-1.162c-.404-.206-.835-.288-1.303-.327A22.07 22.07 0 0014.368 2.5H5.632c-.671 0-1.225 0-1.676.037-.468.038-.899.12-1.303.327a2.917 2.917 0 00-1.174.998.417.417 0 00-.16.296.417.417 0 00.04.256c.047.09.127.147.287.261l7.313 5.224c.606.432.734.508.848.537a.833.833 0 00.405.003c.114-.028.244-.103.856-.527l7.376-5.105z" fill="#171719" />
                  </svg>
                </div>
                <div className={styles.resendContent}>
                  <strong className={styles.resendTitle}>혹시 인증자가 메세지를 받지 못했나요?</strong>
                  <span className={styles.resendDesc}>메세지는 3번까지 재전송 가능해요 | 남은 전송 횟수 : {remainingSends}</span>
                </div>
                <div className={styles.resendActions}>
                  <button type="button" className={styles.resendBtn} onClick={() => onResend?.()}>메세지 재전송</button>
                  <button type="button" className={styles.directShareBtn} onClick={() => onDirectShare?.()}>직접 전달하기</button>
                </div>
              </div>
            )}
          </div>


          <div className={styles.actionArea}>
            {isResubmitExpired && (
              <p className={styles.resubmitExpiredText}>재요청 기간이 만료되었습니다</p>
            )}
            {status === 'rejected' && !isResubmitExpired ? (
              <button
                type="button"
                className={supplementFiles.length > 0 ? styles.ctaButton : styles.ctaButtonDisabled}
                disabled={supplementFiles.length === 0}
                onClick={() => onResubmit?.(supplementFiles)}
              >
                {config.ctaSubmit || '제출하기'}
              </button>
            ) : (status === 'failed' || (status === 'rejected' && isResubmitExpired)) ? (() => {
              const canDispute = verification.can_dispute !== false
                && (status === 'failed' ? !isExpired(verification.dispute_deadline) : true);
              return canDispute ? (
                <div className={styles.actionRow}>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => hasDispute ? setShowDisputeResult(true) : setShowDispute(true)}
                  >
                    {hasDispute ? '이의 신청 완료' : '이의 신청'}
                  </button>
                  <button type="button" className={styles.ctaButton} onClick={() => onRenew?.()}>
                    재인증 요청
                  </button>
                </div>
              ) : (
                <button type="button" className={styles.ctaButton} onClick={() => onRenew?.()}>
                  재인증 요청
                </button>
              );
            })() : (status === 'completed' && verification.can_request) ? (
              <button type="button" className={styles.ctaButton} onClick={() => onRenew?.()}>
                인증 갱신하기
              </button>
            ) : (
              <button type="button" className={styles.ctaButton} onClick={handleCta}>
                {config.cta}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default VerificationStatusModal;
